import { useMemo } from 'react'
import { getCommonBase } from 'uniswap/src/constants/routing'
import { useUniswapContextSelector } from 'uniswap/src/contexts/UniswapContext'
import { useTokenQuery, useTokensQuery } from 'uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { CurrencyInfo } from 'uniswap/src/features/dataApi/types'
import { currencyIdToContractInput, gqlTokenToCurrencyInfo } from 'uniswap/src/features/dataApi/utils'
import { getPreferredTokenLogoUrl } from 'uniswap/src/features/tokens/getPreferredTokenLogoUrl'
import {
  buildNativeCurrencyId,
  buildWrappedNativeCurrencyId,
  currencyIdToAddress,
  currencyIdToChain,
  isNativeCurrencyAddress,
} from 'uniswap/src/utils/currencyId'
import { useMemoCompare } from 'utilities/src/react/hooks'

function areCurrencyIdsEqual(previous: string[] | undefined, next: string[]): boolean {
  return previous?.length === next.length && previous.every((currencyId, index) => currencyId === next[index])
}

export function useCurrencyInfo(
  _currencyId?: string,
  options?: { refetch?: boolean; skip?: boolean },
): Maybe<CurrencyInfo> {
  const searchLocalTokenSelectorResults = useUniswapContextSelector((ctx) => ctx.searchLocalTokenSelectorResults)
  const { data } = useTokenQuery({
    variables: currencyIdToContractInput(_currencyId ?? ''),
    skip: !_currencyId || options?.skip,
    fetchPolicy: options?.refetch ? 'cache-and-network' : 'cache-first',
  })

  return useMemo(() => {
    if (!_currencyId) {
      return undefined
    }

    const chainId = currencyIdToChain(_currencyId)
    let address: Address | undefined
    try {
      address = currencyIdToAddress(_currencyId)
    } catch (error) {
      return undefined
    }
    if (chainId && address) {
      const commonBase = getCommonBase(chainId, isNativeCurrencyAddress(chainId, address), address)
      if (commonBase) {
        // Creating new object to avoid error "Cannot assign to read only property"
        const copyCommonBase = { ...commonBase }
        // Related to TODO(WEB-5111)
        // Some common base images are broken so this'll ensure we read from Ring images
        if (!commonBase.currency.isNative && data?.token?.project?.logoUrl) {
          copyCommonBase.logoUrl = data.token.project.logoUrl
        }
        copyCommonBase.currencyId = _currencyId
        return copyCommonBase
      }
    }

    if (!chainId || !address) {
      return undefined
    }
    const normalizedAddress = address.toLowerCase()

    const localResults = searchLocalTokenSelectorResults?.({
      chainFilter: chainId as UniverseChainId,
      searchFilter: address,
    })
    const localCurrencyInfo = localResults?.find(
      ({ currency }) =>
        currency.chainId === chainId && currency.isToken && currency.address.toLowerCase() === normalizedAddress,
    )
    const gqlCurrencyInfo = data?.token && gqlTokenToCurrencyInfo(data.token)

    if (!gqlCurrencyInfo) {
      return localCurrencyInfo
    }

    return {
      ...gqlCurrencyInfo,
      logoUrl: getPreferredTokenLogoUrl(gqlCurrencyInfo, localCurrencyInfo?.logoUrl),
    }
  }, [_currencyId, data?.token, searchLocalTokenSelectorResults])
}

export function useCurrencyInfos(
  _currencyIds: string[],
  options?: { refetch?: boolean; skip?: boolean },
): Maybe<CurrencyInfo>[] {
  const searchLocalTokenSelectorResults = useUniswapContextSelector((ctx) => ctx.searchLocalTokenSelectorResults)
  const currencyIds = useMemoCompare(() => _currencyIds, areCurrencyIdsEqual)
  const contracts = useMemo(() => currencyIds.map(currencyIdToContractInput), [currencyIds])
  const { data } = useTokensQuery({
    variables: { contracts },
    skip: !currencyIds.length || options?.skip,
    fetchPolicy: options?.refetch ? 'cache-and-network' : 'cache-first',
  })

  return useMemo(() => {
    return (
      data?.tokens?.map((token, index) => {
        const queriedCurrencyInfo = token ? gqlTokenToCurrencyInfo(token) : undefined
        const requestedCurrencyId = currencyIds[index]

        if (!requestedCurrencyId) {
          return queriedCurrencyInfo
        }

        const chainId = currencyIdToChain(requestedCurrencyId)
        let address: Address | undefined
        try {
          address = currencyIdToAddress(requestedCurrencyId)
        } catch {
          return queriedCurrencyInfo
        }

        const commonBase =
          chainId && address ? getCommonBase(chainId, isNativeCurrencyAddress(chainId, address), address) : undefined

        const resolvedCurrencyInfo = commonBase
          ? {
              ...commonBase,
              ...queriedCurrencyInfo,
              currencyId: requestedCurrencyId,
              logoUrl: commonBase.currency.isNative
                ? commonBase.logoUrl
                : queriedCurrencyInfo?.logoUrl || commonBase.logoUrl,
            }
          : queriedCurrencyInfo

        if (!resolvedCurrencyInfo || !chainId || !address || resolvedCurrencyInfo.currency.isNative) {
          return resolvedCurrencyInfo
        }

        const normalizedAddress = address.toLowerCase()
        const localCurrencyInfo = searchLocalTokenSelectorResults?.({
          chainFilter: chainId as UniverseChainId,
          searchFilter: address,
        })?.find(
          ({ currency }) =>
            currency.chainId === chainId && currency.isToken && currency.address.toLowerCase() === normalizedAddress,
        )

        return {
          ...resolvedCurrencyInfo,
          logoUrl: getPreferredTokenLogoUrl(resolvedCurrencyInfo, localCurrencyInfo?.logoUrl),
        }
      }) ?? []
    )
  }, [currencyIds, data, searchLocalTokenSelectorResults])
}

export function useNativeCurrencyInfo(chainId: UniverseChainId): Maybe<CurrencyInfo> {
  const nativeCurrencyId = buildNativeCurrencyId(chainId)
  return useCurrencyInfo(nativeCurrencyId)
}

export function useWrappedNativeCurrencyInfo(chainId: UniverseChainId): Maybe<CurrencyInfo> {
  const wrappedCurrencyId = buildWrappedNativeCurrencyId(chainId)
  return useCurrencyInfo(wrappedCurrencyId)
}
